import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { SocialGroupCard } from "./social-groups/SocialGroupCard";
import { useFrontendSocialGroups } from "@/hooks/useFrontendSocialGroups";
import { useAuth } from "@/hooks/useAuth";
import { useInteractionSounds } from "@/hooks/useInteractionSounds";
import { motion } from "framer-motion";
import { MessageCircle, ArrowRight, ArrowLeft } from "lucide-react";

interface SocialGroupsProps {
  isRTL: boolean;
}

// Add Your Channel Card
const AddChannelCard = ({ isRTL }: { isRTL: boolean }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { playHoverSound } = useInteractionSounds();

  const handleClick = () => {
    if (!user) {
      navigate("/auth");
    } else {
      navigate("/packages");
    }
  };

  return (
    <motion.div
      className="relative bg-gradient-to-br from-emerald-50 to-green-100 dark:from-emerald-900/20 dark:to-green-900/30 rounded-xl overflow-hidden cursor-pointer border-2 border-dashed border-emerald-300 dark:border-emerald-600 min-h-[280px] flex items-center justify-center"
      style={{
        boxShadow: '8px 8px 16px rgba(0,0,0,0.08), -8px -8px 16px rgba(255,255,255,0.9)',
      }}
      whileHover={{ y: -5, scale: 1.02 }}
      onMouseEnter={() => playHoverSound()}
      onClick={handleClick}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <div className="text-center p-6">
        <motion.div 
          className="w-20 h-20 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-emerald-400 to-green-500 flex items-center justify-center shadow-lg"
          whileHover={{ rotate: 10 }}
        >
          <MessageCircle className="w-10 h-10 text-white" />
        </motion.div>
        <h3 className="text-xl font-bold text-emerald-700 dark:text-emerald-300 mb-2">
          {isRTL ? "أضف قناتك أو حسابك" : "Add Your Channel"}
        </h3>
        <p className="text-sm text-emerald-600/70 dark:text-emerald-400/70 mb-4">
          {isRTL ? "شارك حسابك مع المجتمع" : "Share your account with the community"}
        </p>
        <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-emerald-500 to-green-600 text-white rounded-full text-sm font-medium shadow-md">
          {isRTL ? "ابدأ الآن" : "Start Now"}
          {isRTL ? <ArrowLeft className="w-4 h-4" /> : <ArrowRight className="w-4 h-4" />}
        </div>
      </div>
    </motion.div>
  );
};

export const SocialGroups = ({ isRTL }: SocialGroupsProps) => {
  const { t } = useTranslation();
  const { groups } = useFrontendSocialGroups();

  return (
    <section id="social-groups" className="py-16 bg-white">
      <div className="container mx-auto px-4">
        <h2 className="text-3xl font-bold mb-8 text-center text-gray-800">
          {isRTL ? "مجموعات التواصل الاجتماعي" : "Social Media Groups"}
        </h2>
        <p className="text-center text-gray-600 mb-10 max-w-3xl mx-auto">
          {isRTL 
            ? "انضم إلى مجموعات الواتساب والتليجرام الخاصة بالمهرجانات السعودية للحصول على آخر الأخبار والتحديثات"
            : "Join WhatsApp and Telegram groups for Saudi festivals to get the latest news and updates"
          }
        </p>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {/* Add Your Channel Card */}
          <AddChannelCard isRTL={isRTL} />
          
          {groups.map((group) => (
            <SocialGroupCard 
              key={group.id} 
              group={group} 
              isRTL={isRTL} 
            />
          ))}
        </div>
      </div>
    </section>
  );
};
